import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ModelStepObjective from './steps/ModelStepObjective'
import { HORIZON_MODES, MODEL_TYPES } from '../../../iaeau/constants/IAEauConstants'
import { compact, filter, find, head, includes, isFinite, isNumber, last, minBy, size, startsWith, toLower } from 'lodash'
import ModelStepModel from './steps/ModelStepModel'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ModelStepInputs from './steps/ModelStepInputs'
import IAEauAction from '../../../iaeau/IAEauAction'
import StepperDialog from 'components/modal/StepperDialog'
import { Button } from '@mui/material'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import ModelStepVariable from './steps/ModelStepVariable'
import PiezometerStationAction from 'station/actions/PiezometerStationAction'
import HydrometryAction from 'hydrometry/actions/HydrometryAction'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import PluviometryAction from 'pluviometry/actions/PluviometryAction'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import { execByType, hasLocalisationStation } from 'utils/StationUtils'
import { getDistance, getWGS84Coordinate } from 'utils/mapUtils/CoordinateUtils'
import ToastrAction from 'toastr/actions/ToastrAction'
import ModelStepHorizon from './steps/ModelStepHorizon'
import StationAction from 'station/actions/StationAction'
import { hasModelDataPrerequisites } from 'iaeau/utils/IAEauUtils'

const STEP = {
    OBJECTIVE: 0,
    VARIABLE: 1,
    MODEL: 2,
    INPUTSORHORIZON: 3,
}

const PREDEFINED_MODEL = -1
const NO_MODEL_ID = 0

const DEFAULT_GENERIC_MODEL_DATATYPES = [1]
const AREA_PREFIX = 'AREA'
const SCIKIT_LEARN_TYPE = 'SCIKIT_LEARN'
const IA_MODEL_PREFIX = 'IA'
const METERS_IN_KM = 1000

const parseParams = params => {
    try {
        return JSON.parse(params || '{}')
    } catch (e) {
        return {}
    }
}

const getIdsKey = items => items.map(item => `${item.id}_${item.statusSeverity}_${item.gapDays}`).sort().join(',')

const isAreaStation = (code, name) => startsWith(code, AREA_PREFIX) || startsWith(name, AREA_PREFIX)

const getDistanceInKm = (station, pluvio) => {
    try {
        return getDistance(station, pluvio) / METERS_IN_KM
    } catch (e) {
        return Infinity
    }
}

const findAreaPluvio = (associatedSites, pluviometers, station, hasStationLocalisation) => {
    const areaAssociatedSite = find(associatedSites, site => site.typeName === STATION_TYPE_NAME.pluviometry && isAreaStation(site.stationLinkedCode, site.stationLinkedName))
    if (areaAssociatedSite) {
        return {
            id: areaAssociatedSite.stationLinkedId,
            code: areaAssociatedSite.stationLinkedCode,
            name: areaAssociatedSite.stationLinkedName,
            typeName: STATION_TYPE_NAME.pluviometry,
        }
    }
    const areaPluvios = filter(pluviometers, pluvio => isAreaStation(pluvio.code, pluvio.name))
    const localisedAreaPluvios = hasStationLocalisation ? areaPluvios
        .filter(hasLocalisationStation)
        .map(pluvio => ({ ...pluvio, distance: getDistanceInKm(station, pluvio) }))
        .filter(pluvio => isFinite(pluvio.distance)) : []
    return minBy(localisedAreaPluvios, 'distance') || head(areaPluvios)
}

const IAEauModelStepper = ({
    id,
    stationType,
    isOpen = false,
    setIsOpen = () => {},
    selectedModel,
    setSelectedModel = () => {},
}) => {
    const {
        piezometers,
        pluviometers,
        hydrometricStations,
        piezometer,
        hydrometricStation,
        associatedSites,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometersLight,
        pluviometers: store.PluviometryReducer.pluviometers,
        hydrometricStations: store.HydrometryReducer.hydrometricStations,
        piezometer: store.StationReducer.piezometer,
        hydrometricStation: store.HydrometryReducer.hydrometricStation,
        associatedSites: store.StationReducer.associatedSites,
    }), shallowEqual)

    const [stationStats, setStationStats] = useState([])
    const [blockingDataItems, setBlockingDataItems] = useState([])
    const [warningDataItems, setWarningDataItems] = useState([])

    const dispatch = useDispatch()

    const stationId = parseInt(id, 10)
    const params = useMemo(() => parseParams(selectedModel.params), [selectedModel.params])
    const currentStation = execByType(stationType, {
        piezometry: () => piezometer,
        hydrometry: () => hydrometricStation,
    })
    const hasPrerequisites = hasModelDataPrerequisites(selectedModel)
    const hasBlockingError = hasPrerequisites && !!blockingDataItems.length
    const hasWarningData = hasPrerequisites && !hasBlockingError && !!warningDataItems.length

    const handleAvailabilityChange = useCallback(({ blockingItems = [], warningItems = [] }) => {
        setBlockingDataItems(previousItems => (getIdsKey(previousItems) === getIdsKey(blockingItems) ? previousItems : blockingItems))
        setWarningDataItems(previousItems => (getIdsKey(previousItems) === getIdsKey(warningItems) ? previousItems : warningItems))
    }, [])

    const changeModel = useCallback(changes => setSelectedModel(currentModel => ({ ...currentModel, ...changes })), [])
    const changeParams = useCallback(changes => {
        setSelectedModel(currentModel => ({
            ...currentModel,
            params: JSON.stringify({ ...parseParams(currentModel.params), ...changes }),
        }))
    }, [])

    const onStatsLoaded = stats => {
        setStationStats(stats)
        if (stats.length) {
            const typeId = head(stats).typeId
            changeModel({
                typeId,
                params: JSON.stringify({
                    ...params,
                    stations: params.stations.map(station => (station.typeName === selectedModel.stationType ? { ...station, dataTypes: [typeId] } : station)),
                }),
            })
        }
    }

    const { isLoaded, progress } = useProgressDispatch(() => {
        const isPiezo = stationType === STATION_TYPE_NAME.piezometry
        const isHydro = stationType === STATION_TYPE_NAME.hydrometry

        // TODO Faire le même principe que pour le modèle de météo nappes et supprimer cydreExists de l'initialisation du parent
        // if (isHydro) {
        //     changeModel({ cydreExists: true })
        // }

        return compact([
            !piezometers.length && dispatch(PiezometryAction.fetchPiezometersLight()),
            !hydrometricStations.length && dispatch(HydrometryAction.fetchHydrometricStations()),
            !pluviometers.length && dispatch(PluviometryAction.fetchPluviometers()),
            !!currentStation?.code && !size(associatedSites) && dispatch(StationAction.fetchAssociatedSites(currentStation.code, STATION_TYPE_CONSTANT[stationType])),
            isPiezo && PiezometerStationAction.promisePiezoMeasuresStats(selectedModel.idStation).then(onStatsLoaded),
            isHydro && HydrometryAction.promiseHydroStats(selectedModel.idStation).then(onStatsLoaded),
        ])
    }, [])

    useEffect(() => {
        // ajout auto des stations MF pour le modèle générique et LSTM
        if ((selectedModel.idScenario === PREDEFINED_MODEL || hasPrerequisites) && !!pluviometers.length && size(params.stations) === 1) {
            const hasStationLocalisation = hasLocalisationStation(currentStation)
            const areaPluvio = findAreaPluvio(associatedSites, pluviometers, currentStation, hasStationLocalisation)
            if (!areaPluvio) {
                dispatch(ToastrAction.error(i18n.weatherFranceRadarStationNotFound))
            } else {
                changeParams({
                    stations: [...params.stations, { ...areaPluvio, dataTypes: DEFAULT_GENERIC_MODEL_DATATYPES }],
                    coords: hasStationLocalisation ? getWGS84Coordinate(currentStation) : [],
                })
            }
        }
    }, [params.stations, selectedModel.idScenario, pluviometers, selectedModel.typeModel, associatedSites, hasPrerequisites])

    const onSave = () => {
        const hasAssociatedAreaPluvio = associatedSites.some(site => site.typeName === STATION_TYPE_NAME.pluviometry && isAreaStation(site.stationLinkedCode, site.stationLinkedName))
        if (selectedModel.typeModel === MODEL_TYPES.A3P_CLIMATO_MODEL && !hasAssociatedAreaPluvio) {
            dispatch(ToastrAction.error(i18n.climatoModelAreaStationMissing))
            return
        }
        if (hasBlockingError) {
            const blockingNames = blockingDataItems.map(item => item.name).join(', ')
            const hasExcessiveGaps = blockingDataItems.some(item => isNumber(item.gapDays) && item.gapDays > 10)
            const message = hasExcessiveGaps
                ? `${i18n.cantCreateModelDataGap} (${blockingNames})`
                : `${i18n.cantCreateModelMissingData} (${blockingNames})`
            dispatch(ToastrAction.warning(message))
            return
        }

        const horizonModeLabel = find(HORIZON_MODES, { value: selectedModel.horizonMode })?.label || selectedModel.horizonMode
        const horizonModeInitial = toLower(horizonModeLabel).slice(0, 1)
        const modelPrefix = includes(selectedModel.typeModel, SCIKIT_LEARN_TYPE) ? IA_MODEL_PREFIX : selectedModel.typeModel

        const formattedModel = {
            ...selectedModel,
            name: `${modelPrefix}_${selectedModel.horizon}${horizonModeInitial}`,
            color: find(stationStats, { typeId: selectedModel.typeId })?.color,
        }

        const saveModel = selectedModel.idModel === NO_MODEL_ID ? IAEauAction.createModel : IAEauAction.updateModel
        saveModel(stationType, stationId, formattedModel)
            .then(result => IAEauAction.launchModel(stationType, stationId, { ...formattedModel, idModel: result.id || formattedModel.idModel }))
            .then(() => {
                dispatch(ToastrAction.success(i18n.jobLaunched))
                setIsOpen(false)
                dispatch(IAEauAction.getModels(stationType, stationId))
            })
    }

    const displayInputStep = selectedModel.typeModel === MODEL_TYPES.PERCEPTRON ||
        selectedModel.idScenario === PREDEFINED_MODEL ||
        hasPrerequisites
    const lastStep = (displayInputStep || !!selectedModel.typeModel) && {
        label: displayInputStep ? i18n.modelInputs : i18n.horizon,
        constant: STEP.INPUTSORHORIZON,
    }
    const steps = compact([{
        label: i18n.modellingObjective,
        constant: STEP.OBJECTIVE,
        nextAvailable: !!selectedModel.objective,
    }, {
        label: i18n.variableToBeModelled,
        constant: STEP.VARIABLE,
        nextAvailable: !!selectedModel.typeId,
    }, {
        label: i18n.models,
        constant: STEP.MODEL,
        nextAvailable: !!selectedModel.typeModel,
    }, lastStep])

    return (
        <StepperDialog
            steps={steps}
            open={isOpen}
            title={i18n.newModel}
            closeDialog={() => setIsOpen(false)}
            renderSaveButton={step => (step === last(steps).constant && !!selectedModel.typeModel) && (
                <Button
                    variant='contained'
                    onClick={onSave}
                    color={hasBlockingError ? 'error' : (hasWarningData ? 'warning' : 'primary')}
                >
                    {i18n.register}
                </Button>
            )}
        >
            {step => !isLoaded ? (
                <ProgressCard progress={progress} />
            ) : (
                <>
                    {step === STEP.OBJECTIVE && (
                        <ModelStepObjective
                            selectedModel={selectedModel}
                            changeModel={changeModel}
                        />
                    )}
                    {step === STEP.VARIABLE && (
                        <ModelStepVariable
                            stats={stationStats}
                            selectedModel={selectedModel}
                            changeModel={changeModel}
                            params={params}
                        />
                    )}
                    {step === STEP.MODEL && (
                        <ModelStepModel
                            selectedModel={selectedModel}
                            changeModel={changeModel}
                            changeParams={changeParams}
                            stationType={stationType}
                            stationCode={hydrometricStation?.code}
                        />
                    )}
                    {(displayInputStep && step === STEP.INPUTSORHORIZON) && (
                        <ModelStepInputs
                            selectedModel={selectedModel}
                            changeParams={changeParams}
                            changeModel={changeModel}
                            stations={params.stations}
                            stationType={stationType}
                            id={id}
                            onAvailabilityChange={handleAvailabilityChange}
                        />
                    )}
                    {(!displayInputStep && step === STEP.INPUTSORHORIZON) && (
                        <ModelStepHorizon
                            selectedModel={selectedModel}
                            changeModel={changeModel}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

IAEauModelStepper.propTypes = {
    id: PropTypes.string,
    stationType: PropTypes.string,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    selectedModel: PropTypes.shape({
        idStation: PropTypes.number,
        idModel: PropTypes.number,
        typeId: PropTypes.number,
        horizonMode: PropTypes.string,
        horizon: PropTypes.number,
        stationType: PropTypes.string,
        params: PropTypes.shape({
            stations: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.number,
                typeName: PropTypes.string,
                code: PropTypes.string,
                name: PropTypes.string,
                dataTypes: PropTypes.arrayOf(PropTypes.number),
            })),
        }),
        objective: PropTypes.number,
        name: PropTypes.string,
        color: PropTypes.string,
        typeModel: PropTypes.string,
        idScenario: PropTypes.number,
        calculAuto: PropTypes.bool,
        calculationMode: PropTypes.string,
    }),
    setSelectedModel: PropTypes.func,
}

export default IAEauModelStepper
